import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme as T } from '../../theme';
import { useAuth } from '../../context/AuthContext';
import { useDesktopTopBarProps } from '../../context/DesktopLayoutContext';
import UserProfileDropdown, { getInitials } from './UserProfileDropdown';
import OutletDropdown from '../OutletDropdown';
import { reportsPaths } from '../reports/paths';

const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];

/** Single date presets list used across all pages (desktop and mobile). */
export const DATE_PRESETS_DESKTOP = ['Today', 'Yesterday', '7D', /* '30D', */ 'This Month', 'Custom'];

/** Date presets for mobile — same options in all report pages. Use this from TopBar; do not define per-page. */
export const DATE_PRESETS_MOBILE = ['Today', 'Yesterday', '7D', /* '30D', */ 'This Month', 'Custom'];

/** Presets without "Today" — use on report pages that are not Check-in or Snapshot. */
export const DATE_PRESETS_DESKTOP_NO_TODAY = ['Yesterday', '7D', /* '30D', */ 'This Month', 'Custom'];
export const DATE_PRESETS_MOBILE_NO_TODAY = ['Yesterday', '7D', /* '30D', */ 'This Month', 'Custom'];

/** EOD-specific presets — longer-range historical filters. */
export const DATE_PRESETS_EOD = ['Yesterday', 'This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'This Year', 'Last Year', 'Custom'];

/** Returns today's date as YYYY-MM-DD string (local time). */
const getTodayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Returns yesterday's date as YYYY-MM-DD string (local time). */
const getYesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Format a YYYY-MM-DD string as "Mar 01" for display. */
const fmtLabel = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
};

/** Returns true when the value is a custom range string ("custom:YYYY-MM-DD:YYYY-MM-DD"). */
const isCustomVal = (val: any) => typeof val === 'string' && val.startsWith('custom:');

/** Parse custom range string → { from, to }. Returns empty strings when not a custom value. */
const parseCustom = (val: any) => {
  if (!isCustomVal(val)) return { from: '', to: '' };
  const p = val.split(':');
  return p.length === 3 ? { from: p[1], to: p[2] } : { from: '', to: '' };
};

/**
 * Centralized TopBar: shell + built-in date filters. Same component for desktop and mobile.
 * Desktop: content from props (passed by DesktopLayout from context).
 * Mobile: content from context; back and profile built internally; location selector (OutletDropdown) in third row.
 *
 * @param {Object} props
 * @param {'desktop'|'mobile'} [props.variant] - Layout variant
 * @param {React.ReactNode} [props.leftAddon] - e.g. back button (desktop only)
 * @param {string} [props.breadcrumbs] - e.g. "Dashboard" or "Reports › Menu" (desktop only)
 * @param {string} [props.title] - current page title (desktop only; mobile uses context)
 * @param {string} [props.subtitle] - optional subtitle (desktop only)
 * @param {React.ReactNode} [props.rightAddon] - (desktop only; mobile uses internal profile)
 * @param {Object} [props.dateFilters] - (desktop from props; mobile from context)
 *   @param {boolean} [props.dateFilters.allowFutureDates] - when true, custom picker allows future dates (but both dates must be same side: both past/today OR both future)
 *   @param {boolean} [props.dateFilters.excludeToday] - when true, custom picker caps max date at yesterday (today is excluded)
 * @param {Object} [props.sourceFilters] - { options: string[], value: string, onChange: (v) => void }
 * @param {() => void} [props.onExport] - show Export button if provided
 * @param {React.ReactNode} [props.extraContent] - optional node between date/source filters and Export
 * @param {React.ReactNode} [props.bottomSection] - optional full-width row below main bar (desktop/mobile)
 */
export default function TopBar({
  variant = 'desktop',
  leftAddon,
  breadcrumbs,
  title,
  subtitle,
  rightAddon,
  dateFilters,
  sourceFilters,
  extraContent,
  onExport,
  bottomSection,
}: any) {
  const isMobile = variant === 'mobile';
  const topBarPropsFromContext = useDesktopTopBarProps();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout } = useAuth();

  // Custom date range picker state
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const pickerRef = useRef(null);
  const customBtnRef = useRef<HTMLButtonElement>(null);
  const [pickerFixedPos, setPickerFixedPos] = useState<{ top: number; right: number } | null>(null);
  const todayISO = getTodayISO();
  const yesterdayISO = getYesterdayISO();

  // Close picker on outside click or Escape
  useEffect(() => {
    if (!showCustomPicker) return undefined;
    const onMouseDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowCustomPicker(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowCustomPicker(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showCustomPicker]);

  const dateFiltersSource = isMobile ? topBarPropsFromContext.dateFilters : dateFilters;
  const dateOptions = dateFiltersSource && (dateFiltersSource.options?.length ? dateFiltersSource.options : (isMobile ? DATE_PRESETS_MOBILE : DATE_PRESETS_DESKTOP));
  const allowFutureDates = dateFiltersSource?.allowFutureDates || false;
  const excludeToday = dateFiltersSource?.excludeToday || false;
  const pickerMaxDate = allowFutureDates ? undefined : (excludeToday ? yesterdayISO : todayISO);

  /** Open the custom picker, pre-populating from any existing custom selection. */
  const openCustomPicker = () => {
    const existing = parseCustom(dateFiltersSource?.value);
    const defaultDate = excludeToday ? yesterdayISO : todayISO;
    setFromDate(existing.from || defaultDate);
    setToDate(existing.to || defaultDate);
    if (isMobile && customBtnRef.current) {
      const r = customBtnRef.current.getBoundingClientRect();
      setPickerFixedPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setShowCustomPicker(true);
  };

  /** Apply the chosen custom range. */
  const applyCustom = () => {
    dateFiltersSource.onChange(`custom:${fromDate}:${toDate}`);
    setShowCustomPicker(false);
  };

  // When allowFutureDates is on, both dates must be on the same side: both past/today OR both future — no mixing.
  const canApply = (() => {
    if (!fromDate || !toDate || fromDate > toDate) return false;
    if (allowFutureDates) {
      const fromIsFuture = fromDate > todayISO;
      const toIsFuture = toDate > todayISO;
      if (fromIsFuture !== toIsFuture) return false;
    }
    return true;
  })();

  /** Inline date-range picker popover (shared by desktop and mobile). */
  const customPickerPopover = (
    <div
      ref={pickerRef}
      style={isMobile && pickerFixedPos ? {
        position: 'fixed',
        top: pickerFixedPos.top,
        right: pickerFixedPos.right,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        padding: 16,
        zIndex: 10001,
        minWidth: 230,
        fontFamily: T.f,
      } : {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 6,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        padding: 16,
        zIndex: 200,
        minWidth: 230,
        fontFamily: T.f,
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.t2, marginBottom: 4 }}>From</div>
        <input
          type="date"
          value={fromDate}
          max={pickerMaxDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 6,
            border: `1px solid ${T.border}`,
            fontSize: 12,
            fontFamily: T.f,
            color: T.t1,
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.t2, marginBottom: 4 }}>To</div>
        <input
          type="date"
          value={toDate}
          min={fromDate || undefined}
          max={pickerMaxDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 6,
            border: `1px solid ${T.border}`,
            fontSize: 12,
            fontFamily: T.f,
            color: T.t1,
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />
      </div>
      {allowFutureDates && fromDate && toDate && fromDate <= toDate && ((fromDate > todayISO) !== (toDate > todayISO)) && (
        <div style={{ fontSize: 10, color: '#DC2626', marginBottom: 8 }}>
          Both dates must be either past/today or future — cannot mix.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => setShowCustomPicker(false)}
          style={{
            padding: '5px 12px',
            borderRadius: 6,
            border: `1px solid ${T.border}`,
            background: 'transparent',
            fontSize: 11.5,
            color: T.t2,
            cursor: 'pointer',
            fontFamily: T.f,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canApply}
          onClick={canApply ? applyCustom : undefined}
          style={{
            padding: '5px 12px',
            borderRadius: 6,
            border: 'none',
            background: canApply ? T.accent : T.hover,
            fontSize: 11.5,
            color: canApply ? '#fff' : T.t3,
            cursor: canApply ? 'pointer' : 'not-allowed',
            fontFamily: T.f,
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );

  const [showBottomMenu, setShowBottomMenu] = useState(false);
  const [profileMenuPos, setProfileMenuPos] = useState<{ top: number; right: number } | null>(null);
  const profileAvatarRef = useRef<HTMLDivElement>(null);
  const topbarMobileRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!isMobile || !topbarMobileRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty('--mobile-topbar-height', `${entry.contentRect.height}px`);
    });
    obs.observe(topbarMobileRef.current);
    return () => obs.disconnect();
  }, [isMobile]);

  if (isMobile) {
    const mobileTitle = topBarPropsFromContext.title ?? title ?? 'Reports';
    const mobileDateFilters = topBarPropsFromContext.dateFilters ?? dateFilters;
    const onBack = topBarPropsFromContext.onBack;
    const initials = getInitials(auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'U');
    const displayName = auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'User';
    const userRole = auth?.role ?? auth?.designation ?? 'Restaurant Manager';

    const isSnapshotPage = location.pathname.startsWith('/reports/snapshot');
    const hasCustomBack = typeof onBack === 'function';
    const showBack = hasCustomBack || (!isSnapshotPage && topBarPropsFromContext.showBack !== false);
    const backButton = (
      <button
        type="button"
        onClick={() => (typeof onBack === 'function' ? onBack() : navigate(-1))}
        aria-label="Back"
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: T.surface,
          border: `1px solid ${T.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t3 }}>chevron_left</span>
      </button>
    );

    const mobileCustomActive = isCustomVal(mobileDateFilters?.value);
    const mobileCustomRange = parseCustom(mobileDateFilters?.value);

    return (
      <div
        ref={topbarMobileRef}
        style={{
          background: T.surface,
          borderBottom: `1px solid ${T.divider}`,
          boxShadow: T.tbSh,
          fontFamily: T.f,
          paddingTop: 'var(--offline-banner-height, 0px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px 8px',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {showBack && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>{backButton}</div>}
            <div style={{ fontSize: 22, fontWeight: 700, color: T.t1 }}>{mobileTitle}</div>
          </div>
          {/* Circular Profile Trigger */}
          <div style={{ position: 'relative' }}>
            <div
              ref={profileAvatarRef}
              onClick={() => {
                if (!showBottomMenu && profileAvatarRef.current) {
                  const r = profileAvatarRef.current.getBoundingClientRect();
                  setProfileMenuPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
                }
                setShowBottomMenu(!showBottomMenu);
              }}
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                background: T.hover,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: T.t2,
                cursor: 'pointer',
                border: showBottomMenu ? `1.5px solid ${T.accent}` : `1px solid ${T.divider}`,
              }}
            >
              {initials}
            </div>

            {/* Profile dropdown — appears below the avatar */}
            {showBottomMenu && (
              <>
                <div
                  onClick={() => setShowBottomMenu(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                />
                <div
                  style={{
                    position: 'fixed',
                    top: profileMenuPos?.top ?? 56,
                    right: profileMenuPos ? profileMenuPos.right : 16,
                    width: 190,
                    background: T.surface,
                    borderRadius: 12,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                    border: `1px solid ${T.divider}`,
                    zIndex: 10002,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ padding: '12px 14px 4px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{displayName}</div>
                    <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{userRole}</div>
                  </div>
                  {/* Integrations (incl. Google Business Profile) are super-only and
                      surfaced under Settings — only on the Guest Reviews page. */}
                  {location.pathname === reportsPaths.reviews && SUPER_ROLES.includes(auth?.role ?? '') && (
                    <button
                      onClick={() => { setShowBottomMenu(false); navigate('/integrations'); }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: 'transparent',
                        border: 'none',
                        borderTop: `1px solid ${T.divider}`,
                        cursor: 'pointer',
                        color: T.t1,
                        fontSize: 12,
                        fontWeight: 600,
                        marginTop: 8,
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 16 }}>settings</span>
                      Settings
                    </button>
                  )}
                  <button
                    onClick={() => { logout(); navigate('/login', { replace: true }); }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'transparent',
                      border: 'none',
                      borderTop: `1px solid ${T.divider}`,
                      cursor: 'pointer',
                      color: T.red,
                      fontSize: 12,
                      fontWeight: 600,
                      marginTop: 8,
                    }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 16 }}>logout</span>
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {mobileDateFilters && dateOptions && dateOptions.length > 0 && (
          <div style={{ position: 'relative', padding: '0 16px 8px' }}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {dateOptions.map((opt) => {
                const isActive = mobileDateFilters.value === opt || (opt === 'Custom' && mobileCustomActive);
                return (
                  <button
                    key={opt}
                    ref={opt === 'Custom' ? customBtnRef : undefined}
                    type="button"
                    onClick={() => {
                      if (opt === 'Custom') {
                        openCustomPicker();
                      } else {
                        mobileDateFilters.onChange(opt);
                        setShowCustomPicker(false);
                      }
                    }}
                    style={{
                      padding: '5px 8px',
                      borderRadius: 7,
                      fontSize: 10,
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: 'none',
                      background: isActive ? T.accentSoft : 'transparent',
                      color: isActive ? T.accent : T.t2,
                      fontFamily: T.f,
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
              {mobileCustomActive && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, background: T.accentSoft, fontSize: 10, color: T.accent }}>
                  <span>{fmtLabel(mobileCustomRange.from)} – {fmtLabel(mobileCustomRange.to)}</span>
                  <button
                    type="button"
                    onClick={() => { mobileDateFilters.onChange(dateOptions?.length ? dateOptions[0] : 'Today'); setShowCustomPicker(false); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontSize: 13, lineHeight: 1, padding: '0 0 0 2px', display: 'flex', alignItems: 'center' }}
                    aria-label="Clear custom range"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
            {showCustomPicker && customPickerPopover}
          </div>
        )}

        {topBarPropsFromContext.sourceFilters && topBarPropsFromContext.sourceFilters.options?.length > 0 && (
          <div style={{ padding: '0 16px 8px' }}>
            <div style={{ display: 'flex', background: T.hover, borderRadius: 7, padding: 2 }}>
              {topBarPropsFromContext.sourceFilters.options.map((opt) => {
                const active = topBarPropsFromContext.sourceFilters.value === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => topBarPropsFromContext.sourceFilters.onChange(opt)}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: 'none',
                      background: active ? T.surface : 'transparent',
                      color: active ? T.accent : T.t2,
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                      fontFamily: T.f,
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!topBarPropsFromContext.hideOutlet && (
          <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <OutletDropdown />
            </div>
            {topBarPropsFromContext.outletRightAddon}
          </div>
        )}

        {bottomSection && (
          <div style={{ padding: '0 16px 8px' }}>{bottomSection}</div>
        )}
      </div>
    );
  }

  /* Desktop */
  const desktopCustomActive = isCustomVal(dateFilters?.value);
  const desktopCustomRange = parseCustom(dateFilters?.value);

  return (
    <div
      style={{
        height: 56,
        flexShrink: 0,
        background: T.surface,
        borderBottom: `1px solid ${T.div}`,
        boxShadow: T.tbSh,
        position: 'relative',
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        gap: 16,
        fontFamily: T.f,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto' }}>
        {leftAddon}
        {breadcrumbs != null && breadcrumbs !== '' && (() => {
          const parts = breadcrumbs.split('›').map(s => s.trim());
          return (
            <>
              {parts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ fontSize: 12, color: T.t3 }}>›</span>}
                  <span style={{ fontSize: 12, color: i === parts.length - 1 && parts.length > 1 ? T.accent : T.t3 }}>{part}</span>
                </React.Fragment>
              ))}
              <span style={{ fontSize: 12, color: T.t3 }}>›</span>
            </>
          );
        })()}
        <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{title ?? (breadcrumbs === 'Loyalty' ? 'Loyalty' : 'Dashboard')}</span>
      </div>

      {dateFilters && dateOptions && dateOptions.length > 0 && (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ display: 'flex', gap: 2 }}>
            {dateOptions.map((opt) => {
              const isActive = dateFilters.value === opt || (opt === 'Custom' && desktopCustomActive);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    if (opt === 'Custom') {
                      openCustomPicker();
                    } else {
                      dateFilters.onChange(opt);
                      setShowCustomPicker(false);
                    }
                  }}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 11.5,
                    fontWeight: 500,
                    border: 'none',
                    background: isActive ? T.accentSoft : 'transparent',
                    color: isActive ? T.accent : T.t2,
                    fontFamily: T.f,
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {desktopCustomActive && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: T.accentSoft, fontSize: 11, color: T.accent, marginLeft: 4 }}>
              <span style={{ fontWeight: 500 }}>{fmtLabel(desktopCustomRange.from)} – {fmtLabel(desktopCustomRange.to)}</span>
              <button
                type="button"
                onClick={() => { dateFilters.onChange(dateOptions?.length ? dateOptions[0] : 'Today'); setShowCustomPicker(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.accent, fontSize: 14, lineHeight: 1, padding: '0 0 0 2px', display: 'flex', alignItems: 'center' }}
                aria-label="Clear custom range"
              >
                ×
              </button>
            </div>
          )}
          {showCustomPicker && customPickerPopover}
        </div>
      )}

      {sourceFilters && (
        <div style={{ display: 'flex', background: T.hover, borderRadius: 6, padding: 2 }}>
          {sourceFilters.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => sourceFilters.onChange(opt)}
              style={{
                padding: '4px 12px',
                borderRadius: 5,
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 500,
                border: 'none',
                background: sourceFilters.value === opt ? T.surface : 'transparent',
                color: sourceFilters.value === opt ? T.accent : T.t2,
                boxShadow: sourceFilters.value === opt ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                fontFamily: T.f,
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {extraContent}

      {/* Export button — hidden for now
      {onExport && (
        <button
          type="button"
          onClick={onExport}
          style={{
            padding: '6px 14px',
            borderRadius: 8,
            cursor: 'pointer',
            border: `1px solid ${T.border}`,
            fontSize: 11.5,
            color: T.t2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: T.surface,
            fontFamily: T.f,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path
              d="M6 2v6M3 5l3 3 3-3M2 10h8"
              fill="none"
              stroke={T.t3}
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Export
        </button>
      )}
      */}
    </div>
  );
}
